import imageCompression, { Options } from 'browser-image-compression'
import { supportsHeic } from 'util/browser'
import { UserError } from 'util/error'

export const IMAGE_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/avif',
]
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'avif']

// Форматы, которые сервер не принимает: их надо декодировать в браузере и
// отправлять как JPEG. Оба читаются средствами браузера и ОС, без библиотек.
const NATIVE_DECODE_TYPES = ['image/heic', 'image/heif', 'image/avif']
const NATIVE_DECODE_EXTENSIONS = ['heic', 'heif', 'avif']

export const MAX_RAW_IMAGE_SIZE_MB = 25
const MAX_RAW_IMAGE_SIZE = MAX_RAW_IMAGE_SIZE_MB * 1024 * 1024
const MAX_COMPRESSED_IMAGE_SIZE_MB = 4
const MAX_COMPRESSED_IMAGE_SIZE = MAX_COMPRESSED_IMAGE_SIZE_MB * 1024 * 1024
// Цель сжатия — 4 МБ, но browser-image-compression её не гарантирует.
// Всё, что уложилось в лимит загрузки, отправляем как есть: если фото тяжелее
// 4 МБ, сервер пережмёт его сам перед проксированием в апстрим.
const MAX_UPLOAD_IMAGE_SIZE_MB = 10
const MAX_UPLOAD_IMAGE_SIZE = MAX_UPLOAD_IMAGE_SIZE_MB * 1024 * 1024

const MAX_IMAGE_SIDE = 2000
const JPEG_QUALITY = 0.9

function getExtension(name: string): string {
  if (!name) return ''
  const index = name.lastIndexOf('.')
  if (index < 0) return ''
  return name.substring(index + 1).toLowerCase()
}

function replaceExtension(name: string, ext: string): string {
  if (!name) return name
  const index = name.lastIndexOf('.')
  if (index < 0) return name
  return name.substring(0, index) + '.' + ext
}

function renameFile(file: File, ext: string): File {
  const name = replaceExtension(file.name, ext)
  return new File([file], name, { type: file.type })
}

function checkImageFile(file: File): void {
  const { name, type } = file
  const ext = getExtension(name)
  const _type = type ? IMAGE_TYPES.includes(type) : IMAGE_EXTENSIONS.includes(ext)
  if (!_type) {
    throw new UserError('unsupported_image_type')
  }
  if (file.size > MAX_RAW_IMAGE_SIZE) {
    throw new UserError('too_large_file')
  }
}

function needsNativeDecode(file: File) {
  const { name, type } = file
  const ext = getExtension(name)
  return type ? NATIVE_DECODE_TYPES.includes(type) : NATIVE_DECODE_EXTENSIONS.includes(ext)
}

function checkImageSize(file: File): void {
  if (file.size > MAX_UPLOAD_IMAGE_SIZE) {
    throw new UserError('too_large_file')
  }
}

// Декодирование средствами браузера и ОС, без сторонних библиотек.
// HEIC читает Safari под iOS/macOS — то есть те устройства, которые в этот
// формат и снимают. AVIF читают Chrome 85+, Firefox 93+, Safari 16.4+.
// Если браузер формат не знает, createImageBitmap бросает исключение,
// и мы просим загрузить JPEG.
async function decodeToJpeg(file: File): Promise<File> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch (e) {
    console.error('decode image failed', e)
    throw new UserError('unsupported_image_type')
  }

  try {
    const scale = Math.min(MAX_IMAGE_SIDE / bitmap.width, MAX_IMAGE_SIDE / bitmap.height, 1)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        JPEG_QUALITY,
      ),
    )

    return new File([blob], replaceExtension(file.name, 'jpeg'), { type: 'image/jpeg' })
  } catch (e) {
    console.error('convert image failed', e)
    throw new UserError('corrupted_file')
  } finally {
    bitmap.close()
  }
}

async function compressImage(file: File): Promise<File> {
  try {
    file = renameFile(file, 'jpeg')
    const options: Options = {
      maxSizeMB: MAX_COMPRESSED_IMAGE_SIZE_MB,
      maxWidthOrHeight: MAX_IMAGE_SIDE,
      initialQuality: JPEG_QUALITY,
      fileType: 'image/jpeg',
    }
    return await imageCompression(file, options)
  } catch (e) {
    console.error('compress image failed', e)
    throw new UserError('corrupted_file')
  }
}

async function convertImageFile(file: File): Promise<File> {
  // if (file.size < MAX_COMPRESSED_IMAGE_SIZE) return file
  if (needsNativeDecode(file)) file = await decodeToJpeg(file)
  // if (file.size < MAX_COMPRESSED_IMAGE_SIZE) return file
  return compressImage(file)
}

export async function processImageFile(raw: File): Promise<File> {
  checkImageFile(raw)
  const file = await convertImageFile(raw)
  checkImageSize(file)
  return file
}

// HEIC упоминаем в текстах только там, где устройство его действительно читает.
export function heicKey<K extends string>(key: K): K | `${K}_heic` {
  return supportsHeic() ? `${key}_heic` : key
}
